import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useState, useEffect } from 'react';
import { useAuthStore } from '../../store/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import {
  Package,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Users,
  Music,
  Camera,
  Video,
  Wine,
  UtensilsCrossed,
  X
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const API = RUNTIME_BACKEND_URL;

export default function SalonPackagesPage() {
  const { token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [packages, setPackages] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    capacity: 100,
    table_count: 10,
    chair_count: 100,
    has_orchestra: false,
    has_photography: false,
    has_video: false,
    has_drinks: false,
    has_food: false,
    additional_services: '',
    price: 0,
    deposit_amount: 0,
    notes: ''
  });

  useEffect(() => {
    loadPackages();
  }, []);

  const loadPackages = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/salon/packages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPackages(data);
      }
    } catch (error) {
      toast.error('Paketler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      capacity: 100,
      table_count: 10,
      chair_count: 100,
      has_orchestra: false,
      has_photography: false,
      has_video: false,
      has_drinks: false,
      has_food: false,
      additional_services: '',
      price: 0,
      deposit_amount: 0,
      notes: ''
    });
    setEditingPackage(null);
  };

  const openDialog = (pkg = null) => {
    if (pkg) {
      setEditingPackage(pkg);
      setFormData({
        name: pkg.name || '',
        capacity: pkg.capacity || 100,
        table_count: pkg.table_count || 10,
        chair_count: pkg.chair_count || 100,
        has_orchestra: pkg.has_orchestra || false,
        has_photography: pkg.has_photography || false,
        has_video: pkg.has_video || false,
        has_drinks: pkg.has_drinks || false,
        has_food: pkg.has_food || false,
        additional_services: pkg.additional_services || '',
        price: pkg.price || 0,
        deposit_amount: pkg.deposit_amount || 0,
        notes: pkg.notes || ''
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Paket adı zorunludur');
      return;
    }

    setSaving(true);
    try {
      const url = editingPackage 
        ? `${API}/api/salon/packages/${editingPackage.id}`
        : `${API}/api/salon/packages`;
      
      const res = await fetch(url, {
        method: editingPackage ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!res.ok) throw new Error('Kaydedilemedi');

      toast.success(editingPackage ? 'Paket güncellendi' : 'Paket oluşturuldu');
      setIsDialogOpen(false);
      resetForm();
      loadPackages();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu paketi silmek istediğinize emin misiniz?')) return;

    try {
      const res = await fetch(`${API}/api/salon/packages/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Silinemedi');

      toast.success('Paket silindi');
      loadPackages();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="salon-packages-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold">Salon Paketleri</h1>
        <Button onClick={() => openDialog()} data-testid="new-package-btn">
          <Plus className="h-4 w-4 mr-2" />
          Yeni Paket
        </Button>
      </div>

      {packages.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Henüz paket oluşturulmamış.</p>
            <Button onClick={() => openDialog()} className="mt-4">
              <Plus className="h-4 w-4 mr-2" />
              İlk Paketi Oluştur
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {packages.map((pkg) => (
            <Card key={pkg.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-lg">{pkg.name}</CardTitle>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openDialog(pkg)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-destructive"
                      onClick={() => handleDelete(pkg.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span>{pkg.capacity} Kişilik</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {pkg.table_count} Masa • {pkg.chair_count} Sandalye
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {pkg.has_orchestra && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 rounded-full text-xs">
                        <Music className="h-3 w-3" /> Orkestra
                      </span>
                    )}
                    {pkg.has_photography && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 rounded-full text-xs">
                        <Camera className="h-3 w-3" /> Fotoğraf
                      </span>
                    )}
                    {pkg.has_video && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 rounded-full text-xs">
                        <Video className="h-3 w-3" /> Video
                      </span>
                    )}
                    {pkg.has_drinks && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 rounded-full text-xs">
                        <Wine className="h-3 w-3" /> İçecek
                      </span>
                    )}
                    {pkg.has_food && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 rounded-full text-xs">
                        <UtensilsCrossed className="h-3 w-3" /> Yemek
                      </span>
                    )}
                  </div>
                  
                  <div className="pt-3 border-t">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Fiyat:</span>
                      <span className="text-lg font-bold text-primary">{formatCurrency(pkg.price)}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">Kaparo:</span>
                      <span>{formatCurrency(pkg.deposit_amount)}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPackage ? 'Paketi Düzenle' : 'Yeni Paket Oluştur'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Paket Adı *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Örn: Gold Paket, Premium Paket"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Kapasite (Kişi)</Label>
                <Input
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({...formData, capacity: parseInt(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label>Masa Sayısı</Label>
                <Input
                  type="number"
                  value={formData.table_count}
                  onChange={(e) => setFormData({...formData, table_count: parseInt(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label>Sandalye Sayısı</Label>
                <Input
                  type="number"
                  value={formData.chair_count}
                  onChange={(e) => setFormData({...formData, chair_count: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label>Dahil Hizmetler</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <Music className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Orkestra</span>
                  </div>
                  <Switch
                    checked={formData.has_orchestra}
                    onCheckedChange={(checked) => setFormData({...formData, has_orchestra: checked})}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <Camera className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Fotoğraf</span>
                  </div>
                  <Switch
                    checked={formData.has_photography}
                    onCheckedChange={(checked) => setFormData({...formData, has_photography: checked})}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <Video className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Video/Kamera</span>
                  </div>
                  <Switch
                    checked={formData.has_video}
                    onCheckedChange={(checked) => setFormData({...formData, has_video: checked})}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <Wine className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">İçecek Servisi</span>
                  </div>
                  <Switch
                    checked={formData.has_drinks}
                    onCheckedChange={(checked) => setFormData({...formData, has_drinks: checked})}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg col-span-2">
                  <div className="flex items-center gap-2">
                    <UtensilsCrossed className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Yemek</span>
                  </div>
                  <Switch
                    checked={formData.has_food}
                    onCheckedChange={(checked) => setFormData({...formData, has_food: checked})}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Ek Hizmetler</Label>
              <Textarea
                value={formData.additional_services}
                onChange={(e) => setFormData({...formData, additional_services: e.target.value})}
                placeholder="Örn: DJ, Işık Sistemi, Ses Sistemi..."
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fiyat (TL)</Label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label>Kaparo (TL)</Label>
                <Input
                  type="number"
                  value={formData.deposit_amount}
                  onChange={(e) => setFormData({...formData, deposit_amount: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notlar</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Paket hakkında notlar..."
                rows={2}
              />
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                İptal
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {editingPackage ? 'Güncelle' : 'Oluştur'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
